import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion } from '@material-ui/core';
import {
  CheckCircle,
  HourglassFull,
  AddCircleOutline
} from '@material-ui/icons';
import { useAuth0 } from '../../Auth0Provider';
import { InsightDispatch, iRootState } from '../../redux/store';
import ShareSectionHeader from './ShareSectionHeader';
import ShareSectionList from './ShareSectionList';
import { approvedMfrStorageKey, AmplitudeEventKeys } from '../../Constants';
import { ManufacturerItem } from '../../redux/models/shareDataManufacturers';
import RepMfrDataAccessStatus from '../../RepMfrDataAccessStatuses';
import trackAmplitudeEvent from './trackAmplitudeEvents';
import ShareConsentSection from './ShareConsentSection';

enum Actions {
  None = 'none',
  Approve = 'approve',
  Share = 'share'
}

const MfrsRequestDataSelector = function MfrsRequestDataSelector(): React.ReactElement | null {
  const [actionId, setActionId] = React.useState<Actions>(Actions.None);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const companyId = useSelector(
    (state: iRootState) => state.companyInfo.companyId
  );
  const allAvailableManufacturers = useSelector(
    (state: iRootState) => state.shareDataManufacturers.manufacturers
  );
  const isMfrChecked = useSelector(
    (state: iRootState) => state.shareDataManufacturers.isMfrChecked
  );
  const messageEndRef = React.useRef<HTMLInputElement>(null);
  const scrollToBottom = (): void => {
    messageEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'end'
    });
  };

  if (isMfrChecked) {
    setTimeout(scrollToBottom, 500);
  }

  const { dataAccessToken } = useAuth0();
  const dispatch = useDispatch<InsightDispatch>();

  const getApprovedMfrs = function getApprovedMfrs(): ManufacturerItem[] {
    const previousApprovedMfrs = localStorage.getItem(approvedMfrStorageKey);
    if (previousApprovedMfrs) {
      return JSON.parse(previousApprovedMfrs);
    }
    return [];
  };

  const getMfrs = useCallback(() => {
    dispatch.shareDataManufacturers.getAvailableRepManufacturers({
      companyId,
      dataAccessToken
    });
  }, [companyId, dataAccessToken, dispatch]);

  const mapMfrsByStatus = function mapMfrsByStatus(
    statuses: (string | null)[]
  ): { id: string; text: string; preselected: boolean }[] {
    return allAvailableManufacturers
      .filter(r => statuses.includes(r.status))
      .map(m => ({
        id: m.manufacturerId,
        text: m.manufacturerName,
        preselected: false
      }));
  };

  const oldApprovedMfrs = getApprovedMfrs();
  const selectableManufacturers = mapMfrsByStatus([
    '',
    null,
    RepMfrDataAccessStatus.RevokedByRep,
    RepMfrDataAccessStatus.RevokedByMfr,
    RepMfrDataAccessStatus.Rejected
  ]);

  const pendingManufacturers = mapMfrsByStatus([
    RepMfrDataAccessStatus.Pending
  ]);

  const approvedManufacturers = mapMfrsByStatus([
    RepMfrDataAccessStatus.Approved
  ]).map(r => ({
    ...r,
    isNew: !oldApprovedMfrs.some(old => old.manufacturerId === r.id)
  }));

  const newApprovedMfrs = approvedManufacturers.filter(
    value => !oldApprovedMfrs.map(a => a.manufacturerId).includes(value.id)
  );

  const updateStatusData = function updateStatusData(
    status: string,
    event: string
  ): (ids: string[]) => Promise<void> {
    return async (ids: string[]): Promise<void> => {
      dispatch.loading.setLoading(true);

      try {
        const mfrStatusList = ids.map(id => ({
          manufacturerId: id,
          status
        }));

        await dispatch.shareDataManufacturers.shareData({
          mfrStatusList,
          dataAccessToken
        });

        dispatch.shareDataManufacturers.checkConsentAcceptance(false);
        dispatch.shareDataManufacturers.setIsMfrChecked(false);

        trackAmplitudeEvent(ids, event, dispatch);
        getMfrs();
      } finally {
        dispatch.loading.setLoading(false);
      }
    };
  };

  const triggerAction = function triggerAction(
    action: Actions,
    ids: string[]
  ): void {
    setActionId(action);
    setSelectedIds(ids);
    dispatch.shareCenter.openCloseConsentModal(true);
  };

  const approvedMfrsActions = [
    {
      text: 'Revoke',
      callback: updateStatusData(
        RepMfrDataAccessStatus.RevokedByRep,
        AmplitudeEventKeys.RevokeButtonClicked
      )
    }
  ];

  const pendingMfrsActions = [
    {
      text: 'Approve',
      callback: (ids: string[]): void => {
        triggerAction(Actions.Approve, ids);
      }
    },
    {
      text: 'Deny',
      callback: updateStatusData(
        RepMfrDataAccessStatus.Rejected,
        AmplitudeEventKeys.DenyButtonClicked
      )
    }
  ];

  const selectableMfrsActions = [
    {
      text: 'Share My Data',
      callback: (ids: string[]): void => {
        triggerAction(Actions.Share, ids);
      }
    }
  ];

  const handleCallback = function handleCallback(): void {
    dispatch.shareCenter.openCloseConsentModal(false);

    if (actionId === Actions.Approve) {
      updateStatusData(
        RepMfrDataAccessStatus.Approved,
        AmplitudeEventKeys.ApproveButtonClicked
      )(selectedIds);
    } else if (actionId === Actions.Share) {
      updateStatusData(
        RepMfrDataAccessStatus.Approved,
        AmplitudeEventKeys.ShareMyDataButtonClicked
      )(selectedIds);
    }

    setActionId(Actions.None);
  };

  useEffect(() => {
    const saveSharedMfrs = function saveSharedMfrs(
      approvedRepsArg: ManufacturerItem[]
    ): void {
      const sharedMfrs = JSON.stringify([...approvedRepsArg]);
      localStorage.setItem(approvedMfrStorageKey, sharedMfrs);
    };
    saveSharedMfrs(
      approvedManufacturers.map(r => ({
        manufacturerId: r.id,
        manufacturerName: r.text,
        status: RepMfrDataAccessStatus.Approved
      }))
    );
  }, [approvedManufacturers]);

  useEffect(() => {
    getMfrs();
  }, [getMfrs]);

  return (
    <>
      <Accordion defaultExpanded style={{ padding: 0, margin: 0 }}>
        <ShareSectionHeader
          label='Awaiting Your Response'
          count={pendingManufacturers.length}
          icon={<HourglassFull />}
        />
        <ShareSectionList
          items={pendingManufacturers}
          actions={pendingMfrsActions}
          emptyLabel='No manufacturers are waiting for your approval.'
        />
      </Accordion>

      <Accordion style={{ padding: 0, margin: 0 }}>
        <ShareSectionHeader
          label='Sharing Data'
          count={approvedManufacturers.length}
          newCount={newApprovedMfrs.length}
          icon={<CheckCircle />}
        />
        <ShareSectionList
          items={approvedManufacturers}
          actions={approvedMfrsActions}
          emptyLabel='You are not sharing data with any manufacturer yet.'
        />
      </Accordion>

      <Accordion style={{ padding: 0, margin: 0 }}>
        <ShareSectionHeader
          label='Available to Share'
          count={selectableManufacturers.length}
          icon={<AddCircleOutline />}
        />
        <ShareSectionList
          items={selectableManufacturers}
          actions={selectableMfrsActions}
          emptyLabel='No manufacturers available to share data with.'
        />
      </Accordion>

      <ShareConsentSection callback={handleCallback} />
    </>
  );
};

export default MfrsRequestDataSelector;
